/**
 * User API Client
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 */

import { apiFetch, apiFetchText } from "./client";
import type { UserProfileResponse, UserPreferences } from "./types";

/**
 * Get the current user's profile (identity, preferences, subscription tier,
 * activity counters, timestamps). Name/image/email are owned by Better
 * Auth — use `authClient.updateUser` to change them, not this API.
 */
export async function getUserProfile(): Promise<UserProfileResponse> {
  return apiFetch<UserProfileResponse>("/users/profile");
}

/**
 * Update Batch & Bowl preferences (dietary restrictions, household size,
 * etc.). This endpoint only accepts preference fields — auth-critical
 * fields are rejected by the API's strict validation.
 */
export async function updateUserPreferences(
  preferences: UserPreferences,
): Promise<UserProfileResponse> {
  return apiFetch<UserProfileResponse>("/users/preferences", {
    method: "PUT",
    body: JSON.stringify(preferences),
  });
}

/**
 * Persist the user's own store-walk aisle order for the grocery list
 * (in-store mode, E10). PER-USER deliberately -- it describes MY store's
 * layout, so it is not household-shared. The server accepts only known
 * category names (no duplicates); an empty array resets to the default
 * order. Reads come back on the profile (`aisleOrder`).
 */
export async function updateAisleOrder(
  aisleOrder: string[],
): Promise<{ aisleOrder: string[] }> {
  return apiFetch<{ aisleOrder: string[] }>("/users/aisle-order", {
    method: "PATCH",
    body: JSON.stringify({ aisleOrder }),
  });
}

/**
 * Download the full data export (profile, meal plans, grocery lists,
 * favorites, pantry items, authored recipes) as raw JSON text. Uses
 * `apiFetchText` rather than `apiFetch` because the response is served as
 * a `Content-Disposition: attachment` JSON file, not meant to be parsed
 * and consumed as a typed object here — the caller triggers a browser
 * download with the raw text (see GroceryList.tsx's export flow for the
 * same Blob-download pattern).
 */
export async function exportUserData(): Promise<string> {
  return apiFetchText("/users/export");
}

/**
 * Idempotently mark the preferences wizard as completed (Finish or Skip).
 * The server only stamps the timestamp if it is still null, so calling
 * this more than once is harmless.
 */
export async function completeOnboarding(): Promise<{ onboardingCompletedAt: string }> {
  return apiFetch<{ onboardingCompletedAt: string }>("/users/onboarding/complete", {
    method: "POST",
  });
}
